import { Map } from "immutable";
import { reduxForm } from "redux-form/immutable";

import { addParentByEmail } from "seneca-common/features/parents/features/student-parents/service";
import { ChildConnectionSource } from "seneca-common/features/parents/features/student-parents/service/hooks/types";
import { putMySchoolInfo } from "seneca-common/features/schools/service/mutations";
import isValidFunction from "seneca-common/utils/functions/checks/isValidFunction";

import { makeUpdateNotificationPreferences } from "features/notifications/hooks/useUpdatePreferences";
import { analyticsLogPreferencesChangeOnSignUp } from "features/settings/features/notifications/analytics";
import { optimisticallyUpdateAttributes, userAccountType } from "features/user";

import { signUpFormName as formName } from "../../const";
import { logStudentAddsGuardianEmailOnSignup } from "../analytics";

type Validator = (arg0: Map<string, string>, props: any) => Record<string, any>;
type GetFormDetails = (
  formValues: Map<string, string>,
  props: any
) => Record<string, any>;

const { STUDENT_SIGN_UP_PARENT_EMAIL } = ChildConnectionSource;

export default function makeReduxFormWrapper(
  validator: Validator,
  getFormDetails: GetFormDetails
) {
  const updateEmailPreferences = makeUpdateNotificationPreferences("email");
  const updatePushPreferences = makeUpdateNotificationPreferences("push");

  return reduxForm({
    form: formName,
    validate: validator,
    onSubmit: async (
      formValues: Map<string, string>,
      dispatch: any,
      props: any
    ) => {
      const {
        schoolId,
        guardianEmail,
        isSubscribedToMarketingEmails,
        ...userDetails
      }: Record<string, any> = getFormDetails(formValues, props);

      dispatch(optimisticallyUpdateAttributes(userDetails));

      const isParent = userDetails.type === userAccountType.schoolparent;
      const parentMarketing = !!isSubscribedToMarketingEmails;

      if (isParent) {
        updateEmailPreferences({ parentMarketing });
        updatePushPreferences({ parentMarketing });

        analyticsLogPreferencesChangeOnSignUp({
          field: "parentMarketing",
          checked: parentMarketing
        });
      }

      if (schoolId) putMySchoolInfo(schoolId);

      if (guardianEmail) {
        dispatch(addParentByEmail(guardianEmail, STUDENT_SIGN_UP_PARENT_EMAIL));
        logStudentAddsGuardianEmailOnSignup();
      }

      return userDetails;
    },
    onSubmitSuccess: (userDetails, dispatch, props: any) => {
      const { onExtraSignUpDetailsSuccess } = props;

      if (isValidFunction(onExtraSignUpDetailsSuccess)) {
        onExtraSignUpDetailsSuccess(userDetails);
      }
    }
  });
}
